/**
 * `product_grid` and `collection_rail` (spec 42 R2; design 43 SF4/SF5).
 *
 * Neither section owns card styling — the card is spec 26's and lives in the
 * consuming app, so it arrives as `renderCard`. That keeps one card
 * implementation for the storefront and the composer preview without this
 * package reaching into either.
 *
 * Grid breakpoints are spec 26 R5's (2 / 3 ≥ md / 4 ≥ lg) and are set in
 * `store-sections.css` — inline styles cannot carry media queries.
 */
import type { ReactNode } from "react";
import type { LinkTarget } from "@flint/api-contract/store-home";
import { resolveTarget, isExternal, type TargetContext } from "./resolve-target.js";

export interface SectionListing {
  slug: string;
}

function SectionHeader({
  heading,
  subheading,
  action,
}: {
  heading: string;
  subheading?: string | undefined;
  action?: ReactNode;
}) {
  return (
    <div className="flint-section-head">
      <div>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: "1.5rem",
            color: "var(--ink)",
            margin: 0,
          }}
        >
          {heading}
        </h2>
        {subheading ? (
          <p style={{ fontSize: "0.875rem", color: "var(--muted)", marginTop: "0.25rem" }}>
            {subheading}
          </p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

function TextLink({
  label,
  target,
  ctx,
}: {
  label: string;
  target: LinkTarget;
  ctx: TargetContext;
}) {
  const href = resolveTarget(target, ctx);
  if (!href) return null;
  return (
    <a
      href={href}
      {...(isExternal(target) ? { rel: "noopener noreferrer", target: "_blank" } : {})}
      style={{
        fontSize: "0.875rem",
        color: "var(--accent)",
        textDecoration: "none",
        whiteSpace: "nowrap",
      }}
    >
      {label} →
    </a>
  );
}

export function ProductGridSection<T extends SectionListing>({
  heading,
  subheading,
  listings,
  footerLink,
  ctx,
  renderCard,
}: {
  heading: string;
  subheading?: string | undefined;
  /** Defaulted: a config-shaped section (composer preview) carries no data. */
  listings?: T[];
  footerLink?: { label: string; target: LinkTarget } | undefined;
  ctx: TargetContext;
  renderCard: (listing: T) => ReactNode;
}) {
  // Server-side resolution already drops empty sections (R12); this is the
  // belt-and-braces so a heading can never render over nothing — and it keeps
  // a config-shaped section from throwing on an absent `listings`.
  if (!listings || listings.length === 0) return null;

  return (
    <section aria-label={heading}>
      <SectionHeader heading={heading} subheading={subheading} />
      <div className="flint-card-grid">
        {listings.map((l) => (
          <div key={l.slug}>{renderCard(l)}</div>
        ))}
      </div>
      {footerLink ? (
        <div style={{ marginTop: "1rem", textAlign: "center" }}>
          <TextLink label={footerLink.label} target={footerLink.target} ctx={ctx} />
        </div>
      ) : null}
    </section>
  );
}

export interface SectionRail<T> {
  slug: string;
  name: string;
  type: string;
  listingCount: number;
  listings: T[];
}

/**
 * One rail per visible collection. Header anatomy — name, phase context,
 * "View all ({count}) →" — is spec 27 R2 verbatim; only the page position it
 * occupies is this spec's.
 */
export function CollectionRailSection<T extends SectionListing>({
  rails,
  ctx,
  renderCard,
  phaseLabel,
}: {
  /** Defaulted for the same reason as `listings` above. */
  rails?: SectionRail<T>[];
  ctx: TargetContext;
  renderCard: (listing: T) => ReactNode;
  /** Spec 27 R2's phase copy; the rail is context-blind without it. */
  phaseLabel?: (rail: SectionRail<T>) => string | undefined;
}) {
  const visible = (rails ?? []).filter((r) => r.listings.length > 0);
  if (visible.length === 0) return null;

  // Every visible collection is its own rail, but they all live inside one
  // `collection_rail` section — so the section wrapper's spacing sits around the
  // whole group, not between the rails. Space them by the same section gap so
  // Core → AW26 → SS26 don't read as one crammed block.
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-section-gap, 2rem)" }}>
      {visible.map((rail) => (
        <section key={rail.slug} aria-label={rail.name}>
          <SectionHeader
            heading={rail.name}
            subheading={phaseLabel?.(rail)}
            action={
              <TextLink
                label={`View all (${rail.listingCount})`}
                target={{ kind: "collection", slug: rail.slug }}
                ctx={ctx}
              />
            }
          />
          {/* A rail is a single horizontal line-sheet, not a wrapping grid —
              5+ items would otherwise spill into a ragged second row and leave
              a gap before the next section. */}
          <div className="flint-rail">
            {rail.listings.map((l) => (
              <div key={l.slug}>{renderCard(l)}</div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
